import type { HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { catchError, from, switchMap, throwError } from 'rxjs';

import { AuthStore } from '../auth/auth.store';
import { AppConfigService } from '../config/app-config.service';

/**
 * Paths de auth donde un 401 significa "credencial inválida", NO "token vencido":
 * refrescar ahí sería un loop sin sentido. El error debe llegar tal cual al caller
 * (p.ej. el form de login lo traduce a mensaje amigable).
 */
const AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/sso', '/auth/logout'] as const;

/** Misma clave que usa auth.guard.ts para el post-login redirect. */
const REDIRECT_URL_KEY = 'pvp:redirect-url';

/**
 * Refresh reactivo ante 401: si NUESTRA API rechaza el bearer, intenta UN
 * refresh (single-flight vía `AuthStore.refreshShared()`) y reintenta la request
 * con el token nuevo. Si el refresh falla o el retry vuelve a dar 401, la sesión
 * está muerta: logout + redirect a `/auth/login` (guardando la URL actual para
 * volver tras el login, igual que auth.guard.ts) y se re-lanza el error.
 *
 * Alcance:
 * - Solo requests a la API propia (mismo filtro que auth.interceptor.ts).
 * - Solo 401. Excluye los endpoints de auth (ver AUTH_PATHS).
 * - Dev-session: passthrough total — refresh devolvería el mismo devToken y
 *   logout() es un no-op en ese modo (navegar al login con la sesión intacta
 *   produce un loop login↔dashboard). Los 401 propagan como antes del feature.
 *
 * Semántica de `refreshShared()`: token → retry; `null` → sesión MUERTA
 * (logout + login); THROW → fallo TRANSITORIO (red/5xx del refresh): se propaga
 * el 401 original SIN desloguear — el refresh token sigue siendo válido.
 *
 * POSICIÓN EN LA CADENA (ver http.providers.ts): va ÚLTIMO (el más interno):
 * - Ve el 401 ANTES que nadie: un 401 recuperado nunca llega al error
 *   interceptor (nada que toastear/loguear) ni al retry.
 * - El retry de GETs (5xx/red) queda AFUERA: cada reintento suyo pasa de nuevo
 *   por acá, pero jamás duplica refreshes (single-flight) ni al revés (401 no
 *   es retriable para retryInterceptor).
 * - El reintento post-refresh via `next()` va directo al backend con los headers
 *   ya puestos por la cadena (trace-id, tenant) y el bearer nuevo pisado acá.
 */
export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthStore);
  const config = inject(AppConfigService);
  const router = inject(Router);

  const cfg = config.config();
  const bases = [cfg.apiBaseUrl, ...(cfg.apiFailoverUrl ? [cfg.apiFailoverUrl] : [])];

  const isOwnApi = bases.some((base) => req.url.startsWith(base));
  if (!isOwnApi) return next(req);

  // Dev-session: sin refresh ni logout (ambos son no-ops con devToken activo);
  // intervenir acá solo produce loops de navegación. Ver docstring.
  if (auth.isDevSession()) return next(req);

  const isAuthEndpoint = bases.some((base) =>
    AUTH_PATHS.some((path) => req.url.startsWith(`${base}${path}`)),
  );
  if (isAuthEndpoint) return next(req);

  /** Sesión irrecuperable: logout + volver al login recordando dónde estaba. */
  const failSession = (error: unknown): Observable<never> => {
    // refreshLocal() pudo haber navegado ya a /auth/login (logout interno):
    // nunca guardar un destino bajo /auth como redirect post-login.
    const currentUrl = router.url;
    if (!currentUrl.startsWith('/auth')) {
      sessionStorage.setItem(REDIRECT_URL_KEY, currentUrl);
    }
    auth.logout();
    void router.navigateByUrl('/auth/login');
    return throwError(() => error);
  };

  const retryWithToken = (token: string): Observable<HttpEvent<unknown>> => {
    const retried: HttpRequest<unknown> = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
    return next(retried).pipe(
      catchError((retryError: unknown) => {
        if (retryError instanceof HttpErrorResponse && retryError.status === 401) {
          // Token "fresco" también rechazado → la sesión no sirve.
          return failSession(retryError);
        }
        return throwError(() => retryError);
      }),
    );
  };

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }
      return from(
        // token → retry; null → sesión muerta; reject → fallo TRANSITORIO
        // (red/5xx del refresh): NO desloguear, propagar el 401 original.
        auth.refreshShared().then(
          (token) => ({ transient: false as const, token }),
          () => ({ transient: true as const, token: null }),
        ),
      ).pipe(
        switchMap((result) => {
          if (result.transient) return throwError(() => error);
          return result.token ? retryWithToken(result.token) : failSession(error);
        }),
      );
    }),
  );
};
